import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getAccessToken, ApiError } from './api';
import { useCurrentWorkspace } from './workspace';

/* ── Types ──────────────────────────────────────────────────────── */

export type ChatRoomKind = 'dm' | 'group';
export type ChatMessageKind =
  | 'text'
  | 'image'
  | 'file'
  | 'call_started'
  | 'call_ended'
  | 'video_started'
  | 'video_ended';

export interface ChatRoom {
  publicId: string;
  name: string | null;
  kind: ChatRoomKind;
  memberCount: number;
  lastMessageBody: string | null;
  lastMessageAt: string | null;
  lastSenderName: string | null;
  unreadCount: number;
}

export interface ChatMessage {
  publicId: string;
  senderPublicId: string;
  senderName: string;
  senderAvatarUrl: string | null;
  body: string;
  kind: ChatMessageKind;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface RoomMember {
  publicId: string;
  name: string;
  email: string;
  avatarUrl: string | null;
}

/* ── Hooks ──────────────────────────────────────────────────────── */

export function useChatRooms() {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['chat-rooms', ws?.publicId],
    queryFn: () =>
      api<{ data: ChatRoom[] }>(
        `/workspaces/${ws!.publicId}/chat/rooms`,
      ).then((r) => r.data),
    enabled: !!ws,
    refetchInterval: 15_000,
  });
}

/**
 * Total unread chat conversations across ALL workspaces (powers the red badge
 * on the "Let's Chat" bubble). Not workspace-scoped on purpose — it counts
 * unread chat.message notifications wherever they are, and polls so it updates
 * even for messages that arrived in a workspace you're not connected to.
 */
export function useTotalChatUnread() {
  return useQuery({
    queryKey: ['chat-count'],
    queryFn: () => api<{ unread: number }>('/notifications/chat-count'),
    staleTime: 10_000,
    refetchInterval: 20_000,
  });
}

/** Shape of an attachment message's metadata (image / video / document). */
export interface ChatAttachmentMeta {
  url: string;
  filename: string;
  mime: string;
  bytes: number;
  media: 'image' | 'video' | 'file';
  storageKey?: string;
}

export interface RoomMediaItem {
  publicId: string;
  senderName: string;
  kind: ChatMessageKind;
  metadata: ChatAttachmentMeta | null;
  createdAt: string;
}

/** Upload an image / video / document to a room; it's posted as a message. */
export function useUploadChatAttachment(roomPublicId: string | null) {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      if (!ws || !roomPublicId) throw new Error('No room selected');
      const form = new FormData();
      form.append('file', file);
      const token = getAccessToken();
      const res = await fetch(
        `/api/workspaces/${ws.publicId}/chat/rooms/${roomPublicId}/attachments`,
        {
          method: 'POST',
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: form,
        },
      );
      const text = await res.text();
      let parsed: unknown = null;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch {
        parsed = text;
      }
      if (!res.ok) throw new ApiError(res.status, parsed ?? text);
      return parsed as ChatMessage;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-messages', ws?.publicId, roomPublicId] });
      qc.invalidateQueries({ queryKey: ['chat-rooms', ws?.publicId] });
      qc.invalidateQueries({ queryKey: ['room-media', ws?.publicId, roomPublicId] });
    },
  });
}

/** All media + documents shared in a room (for the "View media & docs" view). */
export function useRoomMedia(roomPublicId: string | null, enabled = true) {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['room-media', ws?.publicId, roomPublicId],
    queryFn: () =>
      api<{ data: RoomMediaItem[] }>(
        `/workspaces/${ws!.publicId}/chat/rooms/${roomPublicId}/media`,
      ).then((r) => r.data),
    enabled: !!ws && !!roomPublicId && enabled,
  });
}

/** Search messages within a single conversation (server-side, whole room). */
export function useSearchRoomMessages(roomPublicId: string | null, q: string) {
  const ws = useCurrentWorkspace();
  const term = q.trim();
  return useQuery({
    queryKey: ['chat-search', ws?.publicId, roomPublicId, term],
    queryFn: () =>
      api<{ data: ChatMessage[] }>(
        `/workspaces/${ws!.publicId}/chat/rooms/${roomPublicId}/messages?limit=50&q=${encodeURIComponent(term)}`,
      ).then((r) => r.data),
    enabled: !!ws && !!roomPublicId && term.length >= 2,
    staleTime: 5_000,
  });
}

export function useChatMessages(roomPublicId: string | null, limit = 50) {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['chat-messages', ws?.publicId, roomPublicId],
    queryFn: () =>
      api<{ data: ChatMessage[] }>(
        `/workspaces/${ws!.publicId}/chat/rooms/${roomPublicId}/messages?limit=${limit}`,
      ).then((r) => r.data),
    enabled: !!ws && !!roomPublicId,
    refetchInterval: 5_000,
  });
}

export function useRoomMembers(roomPublicId: string | null) {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['chat-members', ws?.publicId, roomPublicId],
    queryFn: () =>
      api<{ data: RoomMember[] }>(
        `/workspaces/${ws!.publicId}/chat/rooms/${roomPublicId}/members`,
      ).then((r) => r.data),
    enabled: !!ws && !!roomPublicId,
  });
}

export function useSendMessage(roomPublicId: string) {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { body: string; kind?: ChatMessageKind; metadata?: Record<string, unknown> }) =>
      api<ChatMessage>(
        `/workspaces/${ws!.publicId}/chat/rooms/${roomPublicId}/messages`,
        { method: 'POST', body: input },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-messages', ws?.publicId, roomPublicId] });
      qc.invalidateQueries({ queryKey: ['chat-rooms', ws?.publicId] });
    },
  });
}

export function useStartDm() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userPublicId: string) =>
      api<{ publicId: string }>(
        `/workspaces/${ws!.publicId}/chat/rooms/dm`,
        { method: 'POST', body: { userPublicId } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-rooms', ws?.publicId] });
    },
  });
}

export function useCreateGroup() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; memberPublicIds: string[] }) =>
      api<{ publicId: string }>(
        `/workspaces/${ws!.publicId}/chat/rooms/group`,
        { method: 'POST', body: input },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-rooms', ws?.publicId] });
    },
  });
}

/** Open (or create) the chat room for a task, with its assignees added. */
export function useTaskChat() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskPublicId: string) =>
      api<{ roomPublicId: string | null }>(
        `/workspaces/${ws!.publicId}/chat/task-room`,
        { method: 'POST', body: { taskPublicId } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-rooms', ws?.publicId] });
    },
  });
}

export function useMarkRead(roomPublicId: string) {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api(`/workspaces/${ws!.publicId}/chat/rooms/${roomPublicId}/read`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-rooms', ws?.publicId] });
      qc.invalidateQueries({ queryKey: ['chat-count'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useAddMember(roomPublicId: string) {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userPublicId: string) =>
      api(`/workspaces/${ws!.publicId}/chat/rooms/${roomPublicId}/members`, {
        method: 'POST',
        body: { userPublicId },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-members', ws?.publicId, roomPublicId] });
    },
  });
}
